import React, { useEffect } from 'react';
import { useTemplateStore } from 'Hooks/zustand/useTemplateStore';
import { useParams } from 'react-router-dom';
import { useQueryParam } from 'Hooks/useQueryParam';
import useGetList from 'Hooks/query-hooks/list/useGetList';
import useGetParameters from 'Hooks/query-hooks/parameters/useGetParameters';
import useGetRule from 'Hooks/query-hooks/global-rules/useGetRule';
import useGetReduxUsers from 'Hooks/reduxHelperHooks/useGetReduxUsers';
import useGetMerchantOverrides from 'Hooks/query-hooks/merchants/useGetMerchantOverrides';
import useGetTenantGroupOverrideRules from 'Hooks/query-hooks/tenant/useGetTenantGroupOverrideRules';
import useGetLandlordInfo from 'Hooks/query-hooks/tenant/useGetLandlordInfo';
import useGetConclusionFlags from 'Hooks/query-hooks/useGetConclusionFlags';
import errorController from 'Components/ErrorScreens';
import RouteLoading from 'Components/RouteLoading';
import UpdateSimpleRuleLayout from './UpdateSimpleRuleLayout';

const UpdateSimpleRulePage = () => {
    const { templateRepositoryId } = useTemplateStore.getState().template;
    const { users } = useGetReduxUsers();
    const { data: landlordInfo } = useGetLandlordInfo();
    const { ruleId } = useParams<{ ruleId: string }>();
    const {
        parsed: { selectedRowId }
    } = useQueryParam();
    const isLandlord = !!landlordInfo?.isLandlord;
    const listAPI = useGetList();
    const ruleAPI = useGetRule({
        ruleId,
        users
    });
    const parametersAPI = useGetParameters({
        queryParams: { status: JSON.stringify(['active']) }
    });
    const merchantOverridesAPI = useGetMerchantOverrides();
    const tenantGroupOverrideRulesAPI = useGetTenantGroupOverrideRules(isLandlord);
    const { data: conclusionFlagListData } = useGetConclusionFlags();
    const errorChecks = [parametersAPI.error, listAPI.error, ruleAPI.error, merchantOverridesAPI.error];
    const { hasError, errorCmp } = errorController(errorChecks);

    useEffect(() => {
        // always scroll to the top
        window.scrollTo(0, 0);
    }, []);

    let tenantGroupOverrideData: RevisionTenantGroupOverrides | undefined = {};
    if (isLandlord) {
        errorChecks.push(tenantGroupOverrideRulesAPI.error);
        tenantGroupOverrideData = tenantGroupOverrideRulesAPI.data;
    }

    if (
        listAPI.isLoading ||
        listAPI.isFetching ||
        merchantOverridesAPI.isLoading ||
        merchantOverridesAPI.isFetching ||
        ruleAPI.isLoading ||
        ruleAPI.isFetching ||
        parametersAPI.isFetching ||
        parametersAPI.isLoading ||
        ((tenantGroupOverrideRulesAPI.isLoading || tenantGroupOverrideRulesAPI.isFetching) && isLandlord)
    ) {
        return <RouteLoading />;
    }
    if (hasError) return <>{errorCmp}</>;

    return (
        <UpdateSimpleRuleLayout
            parameterData={parametersAPI.data}
            lData={listAPI.data!}
            currentRule={ruleAPI.data!}
            // TODO: we are not supporting both merchant and tenant group overrides at the same time for now.
            overrideData={isLandlord ? tenantGroupOverrideData! : merchantOverridesAPI.data!}
            selectedRowId={selectedRowId?.toString()}
            repoId={templateRepositoryId!}
            conclusionFlagListData={conclusionFlagListData}
        />
    );
};

export default UpdateSimpleRulePage;
